import SESSION_STORAGE_KEY from "../../constants/storage.constant";
import { API } from "../../constants/constants";
import axios, { AxiosRequestHeaders } from "axios";

/** Read a cookie by name */
const getCookie = (name: string): string | undefined => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop()?.split(";").shift();
    return undefined;
};

/** Common auth headers pulled from cookies/sessionStorage */
const getAuthHeaders = (): Record<string, string | undefined> => ({
    "x-aid": getCookie("aid"),
    "x-rid": getCookie("rid"),
    ssoid: getCookie("ssoid"),
    tpid: getCookie("tpid"),
    sid: window.sessionStorage.getItem(SESSION_STORAGE_KEY.SESSIONID_TOKEN) ?? undefined,
});

/** File inputs you want to attach to variables (GraphQL multipart spec) */
type UploadInput =
    | { path: string; files: File | File[] }         // explicit variable path(s)
    | { path?: "variables.file"; files: File | File[] }; // default path

type GraphQLArgs<V> = {
    query: string;
    variables?: V;
    headers?: AxiosRequestHeaders;
    withCredentials?: boolean;
    uploads?: UploadInput | UploadInput[]; // omit/empty → normal JSON request
};

/** Overloads for nicer inference */
async function MmcGraphQL<T = any, V = Record<string, any>>(args: GraphQLArgs<V>): Promise<T>;
async function MmcGraphQL<T = any, V = Record<string, any>>(
    query: string,
    variables?: V,
    headers?: AxiosRequestHeaders,
    withCredentials?: boolean
): Promise<T>;

/** Single entry that does both JSON and multipart */
async function MmcGraphQL<T = any, V = Record<string, any>>(
    a:
        | string
        | GraphQLArgs<V>,
    b?: V,
    c?: AxiosRequestHeaders,
    d?: boolean
): Promise<T> {
    // Normalize args
    const args: GraphQLArgs<V> =
        typeof a === "string"
            ? { query: a, variables: (b ?? {} as V), headers: c, withCredentials: d }
            : a;

    const {
        query,
        variables = {} as V,
        headers = {},
        withCredentials = true,
        uploads,
    } = args;

    const baseHeaders = getAuthHeaders();

    // If there are no uploads, do a plain JSON POST
    const hasUploads =
        Array.isArray(uploads)
            ? uploads.length > 0
            : !!uploads;

    if (!hasUploads) {
        const mergedHeaders = { ...baseHeaders, ...headers };
        const res = await axios.post(
            API.BASEURL,
            { query, variables },
            { headers: mergedHeaders, withCredentials }
        );
        // Optional: surface GraphQL errors
        if (res.data?.errors) {
            throw new Error(res.data.errors.map((e: any) => e.message).join("; "));
        }
        return res?.data?.data?.response as T;
    }

    // Multipart upload (GraphQL multipart request spec)
    const list: UploadInput[] = (Array.isArray(uploads) ? uploads : [uploads!]).map(u => ({
        path: u.path ?? "variables.file",
        files: u.files,
    }));

    const form = new FormData();

    // Build the files array and the map object
    const fileEntries: { key: string; file: File }[] = [];
    let fileIndex = 0;
    const map: Record<string, string[]> = {};

    for (const { path, files } of list) {
        const arr = Array.isArray(files) ? files : [files];
        for (let i = 0; i < arr.length; i++) {
            const key = String(fileIndex++);
            fileEntries.push({ key, file: arr[i] });
            // If the path points to an array, e.g. variables.files[0], caller can pass that in.
            // For simple "variables.file" we still map each file to that path (server resolver should accept array if needed).
            const safePath = path ?? "variables.file";

            map[key] = [
                arr.length > 1 && !/\[\d+\]$/.test(safePath)
                    ? `${safePath}[${i}]`
                    : safePath
            ];
        }
    }

    // Append files
    for (const { key, file } of fileEntries) {
        form.append(key, file, file.name);
    }

    // operations: the normal JSON payload (variables may include nulls/placeholders for file locations)
    form.append("operations", JSON.stringify({ query, variables }));

    // map: which file index maps to which variable paths
    form.append("map", JSON.stringify(map));

    const mergedHeaders = {
        ...baseHeaders,
        "GraphQL-preflight": 1,          // if your server expects this
        ...headers,
        // IMPORTANT: do NOT set Content-Type manually; let the browser set boundary for multipart
    };

    const res = await axios.post(
        API.BASEURL,
        form,
        { headers: mergedHeaders, withCredentials }
    );

    if (res.data?.errors) {
        throw new Error(res.data.errors.map((e: any) => e.message).join("; "));
    }
    return res?.data?.data?.response as T;
}

export default MmcGraphQL;
